/**
 * Speaker jump nav.
 *
 * Two jobs: the collapsed disclosure on narrow viewports, and moving focus to
 * the section a link points at.
 *
 * The focus move is the part that matters for accessibility. A plain in-page
 * anchor scrolls the viewport but, in several browsers, leaves focus on the
 * link — so a keyboard user who jumps to "Testimonials" then tabs and finds
 * themselves back in the nav, with the page having moved underneath them
 * (WCAG 2.4.3). Sections carry tabindex="-1" so they can receive focus without
 * entering the tab order.
 *
 * The collapse is applied by CSS only once `is-enhanced` is present, which this
 * file adds. Without it the toggle stays hidden and the list stays visible, so
 * a browser that never runs this still gets a working list of links.
 */

const NAV = '.dn-speaker__jump';
const TOGGLE = '.dn-speaker__jump-toggle';
const LINK = '.dn-speaker__jump-link';

/**
 * Whether the visitor has asked for reduced motion.
 *
 * @return {boolean} True when animation should be skipped.
 */
function prefersReducedMotion() {
	return window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
}

/**
 * Wire one jump nav.
 *
 * @param {HTMLElement} nav The nav element.
 */
function initNav( nav ) {
	const toggle = nav.querySelector( TOGGLE );
	const links = nav.querySelectorAll( LINK );

	if ( ! toggle || ! links.length ) {
		return;
	}

	nav.classList.add( 'is-enhanced' );

	const isExpanded = () => 'true' === toggle.getAttribute( 'aria-expanded' );

	const setExpanded = ( expanded ) => {
		toggle.setAttribute( 'aria-expanded', expanded ? 'true' : 'false' );
	};

	toggle.addEventListener( 'click', () => {
		setExpanded( ! isExpanded() );
	} );

	// Escape closes and hands focus back to the control that opened it, rather
	// than leaving it stranded inside a list that is no longer visible.
	document.addEventListener( 'keydown', ( event ) => {
		if ( 'Escape' === event.key && isExpanded() ) {
			setExpanded( false );
			toggle.focus();
		}
	} );

	document.addEventListener( 'click', ( event ) => {
		if ( isExpanded() && ! nav.contains( event.target ) ) {
			setExpanded( false );
		}
	} );

	links.forEach( ( link ) => {
		link.addEventListener( 'click', ( event ) => {
			const id = link.getAttribute( 'href' );

			if ( ! id || '#' !== id.charAt( 0 ) ) {
				return;
			}

			const target = document.querySelector( id );

			if ( ! target ) {
				return;
			}

			event.preventDefault();
			setExpanded( false );

			target.scrollIntoView( {
				behavior: prefersReducedMotion() ? 'auto' : 'smooth',
				block: 'start',
			} );

			/*
			 * focus() would scroll instantly and fight the smooth scroll above,
			 * so the jump is left to scrollIntoView and focus is moved without
			 * a second scroll. The URL is updated separately for the same
			 * reason: assigning location.hash would also jump.
			 */
			target.focus( { preventScroll: true } );

			if ( window.history && window.history.pushState ) {
				window.history.pushState( null, '', id );
			}
		} );
	} );
}

function init() {
	document.querySelectorAll( NAV ).forEach( initNav );
}

if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
